import { useMemo, useEffect, useRef } from 'react';
import { useAuth } from 'oidc-react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAgronodKonto } from '@/contexts/AgronodKontoContext';

/**
 * Hook that injects OIDC access token and selected agronodkonto ID into the API client.
 * Call this in any component that needs authenticated API access.
 *
 * Both values are set synchronously during render to avoid race conditions
 * where queries fire before the headers are set.
 *
 * When the selected konto changes, all React Query caches are invalidated
 * so data is re-fetched for the new konto.
 */
export const useApiClient = () => {
  const { userData } = useAuth();
  const accessToken = userData?.access_token || null;
  const { selectedKontoId } = useAgronodKonto();
  const queryClient = useQueryClient();
  const prevKontoId = useRef(selectedKontoId);

  // Set both headers synchronously during render to avoid race conditions
  useMemo(() => {
    apiClient.setToken(accessToken);
    apiClient.setAgronodKontoId(selectedKontoId);
  }, [accessToken, selectedKontoId]);

  // Invalidate all queries when konto changes so data is re-fetched
  useEffect(() => {
    if (prevKontoId.current !== null && prevKontoId.current !== selectedKontoId) {
      queryClient.invalidateQueries();
    }
    prevKontoId.current = selectedKontoId;
  }, [selectedKontoId, queryClient]);

  return apiClient;
};
